import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { getVoterRegistrationInfo, voterSetPassword } from "../lib/api";
import { Lock, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { PasswordStrength } from "../components/PasswordStrength";
import { LegalFooter } from "../components/LegalFooter";

export default function VoterSetPasswordPage() {
  const { t, i18n } = useTranslation();
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getVoterRegistrationInfo(token);
        setInfo(res.data);
        if (res.data.language && res.data.language !== i18n.language) {
          i18n.changeLanguage(res.data.language);
        }
      } catch (err) {
        setError(err.response?.data?.detail || t('errors.invalidLink'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (info?.require_email && !email.trim()) {
      setError(t('voterSetPassword.emailRequired'));
      return;
    }
    if (password.length < 10 || !/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/[0-9]/.test(password) || !/[!@#$%^&*()\-_=+\[\]{};:'",.<>?/\\|`~]/.test(password)) {
      setError(t('auth.passwordTooWeak'));
      return;
    }
    if (password !== confirmPassword) {
      setError(t('auth.passwordsDoNotMatch'));
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await voterSetPassword(token, password, email.trim());
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || t('errors.generic'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
      <Card className="w-full max-w-md" data-testid="voter-set-password-page">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-3">
            <Lock className="h-6 w-6 text-blue-600" />
          </div>
          <CardTitle className="text-xl">{t('voterSetPassword.createTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          {done ? (
            <div className="text-center space-y-4" data-testid="password-set-success">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-green-800">{t('voterSetPassword.passwordCreated')}</h3>
              <p className="text-slate-600">
                {t('voterSetPassword.canVoteNow', { entity: info?.entity_name })}
              </p>
              <p className="text-sm text-slate-500">{t('voterSetPassword.canClosePage')}</p>
            </div>
          ) : error && !info ? (
            <div className="text-center space-y-4" data-testid="registration-error">
              <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="h-8 w-8 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-red-800">{t('errors.invalidLink')}</h3>
              <p className="text-slate-600">{error}</p>
            </div>
          ) : info ? (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg text-sm text-slate-600">
                <p>{t('voterSetPassword.entityLabel')}: <strong>{info.entity_name}</strong></p>
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                {info?.require_email && (
                <div className="space-y-2">
                  <Label htmlFor="email">{t('common.email')}</Label>
                  <Input
                    id="email"
                    data-testid="voter-email-input"
                    type="email"
                    placeholder={t('voterSetPassword.emailPlaceholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoFocus
                  />
                </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="password">{t('voterSetPassword.newPassword')}</Label>
                  <Input
                    id="password"
                    data-testid="voter-password-input"
                    type="password"
                    placeholder={t('passwordPolicy.minLength', { count: 10 })}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <PasswordStrength password={password} minLength={10} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">{t('voterSetPassword.confirmPassword')}</Label>
                  <Input
                    id="confirmPassword"
                    data-testid="voter-confirm-password-input"
                    type="password"
                    placeholder={t('voterSetPassword.confirmPassword')}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  disabled={submitting}
                  data-testid="voter-set-password-btn"
                >
                  {submitting ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t('common.processing')}
                    </span>
                  ) : (
                    t('voterSetPassword.createTitle')
                  )}
                </Button>
              </form>
            </div>
          ) : null}
        </CardContent>
      </Card>
      <div className="w-full max-w-md mt-4">
        <LegalFooter />
      </div>
    </div>
  );
}
